import * as React from 'react';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Chip from '@mui/material/Chip';
import Button from '@mui/material/Button';
import DeviceDataGettingError from '../placeholders/DeviceDataGettingError';
import TableNoResults from '../placeholders/TableNoResults';
import EntityViewerDialog from '../dialogs/entity-viewer-dialog/EntityViewerDialog';
import ErrorViewerDialog from '../dialogs/error-viewer-dialog/ErrorViewerDialog';
import TablePlaceholder from '../placeholders/TablePlaceholder';
import ActionLogsTableSearch from '../searches/ActionLogsTableSearch';
import ActionLogsExportButton from '../buttons/ActionLogsExportButton';
import { Link } from 'react-router-dom';
import ActionLogsTableFilterMenu from '../filters/ActionLogsTableFilterMenu';
import { visuallyHidden } from '@mui/utils';
import { format, parseISO } from 'date-fns';
import { styled } from '@mui/material/styles';
import { ActionLogsDataType, NormalizedActionLogsDataType } from '../../types/actionLogsTypes';
import { ActionLogsTableFilterDataType } from '../../types/filterTypes';
import tableStyles from './tableStyles';
import { Order } from '../../types/types';
import { stableSort, getComparator } from '../../utils/tableHelpers';
import { normalizeActionLogs } from '../../api/transform';
import { useActionLogsQuery } from '../../hooks/useActionLogsQuery';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LaunchIcon from '@mui/icons-material/Launch';

// Excludes nullable error_type/error_message to satisfy getComparator's
// `string | number | boolean` bound.
type ActionLogsSortableKey = Exclude<keyof ActionLogsDataType, 'error_type' | 'error_message'>;

interface HeadActionLogsCell {
  disablePadding: boolean;
  id: ActionLogsSortableKey;
  label: string;
  numeric: boolean;
}

const headActionLogsCells: readonly HeadActionLogsCell[] = [
  {
    id: 'device_ip',
    numeric: false,
    disablePadding: false,
    label: 'Device IP',
  },
  {
    id: 'device_vendor',
    numeric: false,
    disablePadding: false,
    label: 'Vendor',
  },
  {
    id: 'region',
    numeric: false,
    disablePadding: false,
    label: 'Region',
  },
  {
    id: 'user_action',
    numeric: false,
    disablePadding: false,
    label: 'User action',
  },
  {
    id: 'user_email',
    numeric: false,
    disablePadding: false,
    label: 'Email',
  },
  {
    id: 'user_role',
    numeric: false,
    disablePadding: false,
    label: 'Role',
  },
  {
    id: 'building_code',
    numeric: false,
    disablePadding: false,
    label: 'Building',
  },
  {
    id: 'success',
    numeric: false,
    disablePadding: false,
    label: 'Action status',
  },
  {
    id: 'details',
    numeric: false,
    disablePadding: false,
    label: 'Details',
  },
  {
    id: 'created_at',
    numeric: false,
    disablePadding: false,
    label: 'Performed at',
  },
];

interface ActionLogsTableHeadProps {
  onRequestSort: (event: React.MouseEvent<unknown>, property: ActionLogsSortableKey) => void;
  order: Order;
  orderBy: ActionLogsSortableKey;
}

const ActionLogsTableHead = React.memo(function ActionLogsTableHead(
  props: ActionLogsTableHeadProps
) {
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler =
    (property: ActionLogsSortableKey) => (event: React.MouseEvent<unknown>) => {
      onRequestSort(event, property);
    };

  return (
    <TableHead>
      <TableRow>
        {headActionLogsCells.map((HeadActionLogsCell) => (
          <TableCell
            key={HeadActionLogsCell.id}
            align={HeadActionLogsCell.numeric ? 'right' : 'left'}
            padding={HeadActionLogsCell.disablePadding ? 'none' : 'normal'}
            sortDirection={orderBy === HeadActionLogsCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === HeadActionLogsCell.id}
              direction={orderBy === HeadActionLogsCell.id ? order : 'asc'}
              onClick={createSortHandler(HeadActionLogsCell.id)}
            >
              {HeadActionLogsCell.label}
              {orderBy === HeadActionLogsCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
});

interface TableToolbarProps {
  title: string;
  search: boolean;
  exportData: ActionLogsDataType[];
  searchQuery: string;
  onSearchChange: (query: string) => void;
  filterState: ActionLogsTableFilterDataType;
  activeFilterCount: number;
  onFilterChange: (key: keyof ActionLogsTableFilterDataType, value: string) => void;
  onClearFilter: (key: keyof ActionLogsTableFilterDataType) => void;
  onClearAll: () => void;
}

function TableToolbar(props: TableToolbarProps) {
  const {
    title,
    search,
    exportData,
    searchQuery,
    onSearchChange,
    filterState,
    activeFilterCount,
    onFilterChange,
    onClearFilter,
    onClearAll,
  } = props;
  const fetchTime = format(new Date(), 'EEEE, d MMMM yyyy, HH:mm');

  return (
    <Toolbar>
      <Typography sx={{ flex: '1 1 100%' }} variant="h5" id="tableTitle" component="div">
        {title}: {fetchTime}
      </Typography>
      <ActionLogsExportButton headCells={headActionLogsCells} rows={exportData} />
      {search && <ActionLogsTableSearch value={searchQuery} onChange={onSearchChange} />}
      <ActionLogsTableFilterMenu
        filterState={filterState}
        activeFilterCount={activeFilterCount}
        onFilterChange={onFilterChange}
        onClearFilter={onClearFilter}
        onClearAll={onClearAll}
      />
    </Toolbar>
  );
}

interface ActionLogsTableProps {
  allActionLogs: ActionLogsDataType[];
  isLoading: boolean;
  apiErrorFlag: boolean;
}

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

interface ActionLogsTableRowProps {
  row: NormalizedActionLogsDataType;
  labelId: string;
  onOpenEntityViewer: (details: string) => void;
  onOpenErrorViewer: (row: NormalizedActionLogsDataType) => void;
}

const ActionLogsTableRow = React.memo(function ActionLogsTableRow({
  row,
  labelId,
  onOpenEntityViewer,
  onOpenErrorViewer,
}: ActionLogsTableRowProps) {
  return (
    <StyledTableRow key={labelId} id={labelId}>
      <TableCell>{row.device_ip}</TableCell>
      <TableCell>{row.device_vendor}</TableCell>
      <TableCell>{row.region}</TableCell>
      <TableCell>{row.user_action}</TableCell>
      <TableCell>{row.user_email}</TableCell>
      <TableCell>{row.user_role}</TableCell>
      <TableCell>
        {row.building_code ? (
          <Button
            component={Link}
            to={`/devices/${row.building_code.toUpperCase()}?region=${row.region}`}
            endIcon={<LaunchIcon />}
            size="small"
            variant="text"
            onClick={(e) => e.stopPropagation()}
            sx={{ textTransform: 'none' }}
          >
            {row.building_code}
          </Button>
        ) : (
          '—'
        )}
      </TableCell>
      <TableCell>
        {row.success ? (
          <Chip label="Success" color="success" size="small" />
        ) : row.error_source !== 'none' ? (
          <Chip
            label="Failed"
            color="error"
            size="small"
            icon={<InfoOutlinedIcon sx={tableStyles.chipIcon} />}
            onClick={() => onOpenErrorViewer(row)}
          />
        ) : (
          <Chip label="Failed" color="error" size="small" />
        )}
      </TableCell>
      <TableCell>
        <Button variant="outlined" size="small" onClick={() => onOpenEntityViewer(row.details)}>
          More info
        </Button>
      </TableCell>
      <TableCell>{format(parseISO(`${row.created_at}Z`), 'yyyy-MM-dd HH:mm')}</TableCell>
    </StyledTableRow>
  );
});

export default function ActionLogsTable(props: ActionLogsTableProps) {
  const { allActionLogs, isLoading, apiErrorFlag } = props;
  const {
    filteredLogs,
    searchQuery,
    setSearchQuery,
    filterState,
    setFilterValue,
    clearFilter,
    clearAll,
    activeFilterCount,
  } = useActionLogsQuery(allActionLogs);
  const [order, setOrder] = React.useState<Order>('desc');
  const [orderBy, setOrderBy] = React.useState<ActionLogsSortableKey>('created_at');
  const [page, setPage] = React.useState(0);
  const [dense, setDense] = React.useState(false);
  const [rowsPerPage, setRowsPerPage] = React.useState(30);
  const [entityViewerDialogOpen, setEntityViewerDialogOpen] = React.useState(false);
  const [selectedEntity, setSelectedEntity] = React.useState<string | null>(null);
  const [errorViewerDialogOpen, setErrorViewerDialogOpen] = React.useState(false);
  const [actionErrorData, setActionErrorData] = React.useState<NormalizedActionLogsDataType | null>(
    null
  );

  const handleOpenEntityViewerDialog = React.useCallback((details: string) => {
    setSelectedEntity(details);
    setEntityViewerDialogOpen(true);
  }, []);

  const handleCloseEntityViewerDialog = React.useCallback(() => {
    setEntityViewerDialogOpen(false);
    setSelectedEntity(null);
  }, []);

  const handleOpenErrorViewerDialog = React.useCallback((action: NormalizedActionLogsDataType) => {
    setActionErrorData(action);
    setErrorViewerDialogOpen(true);
  }, []);

  const handleCloseErrorViewerDialog = React.useCallback(() => {
    setErrorViewerDialogOpen(false);
    setActionErrorData(null);
  }, []);

  const handleRequestSort = React.useCallback(
    (event: React.MouseEvent<unknown>, property: ActionLogsSortableKey) => {
      setOrder((prevOrder) => {
        const isAsc = orderBy === property && prevOrder === 'asc';
        return isAsc ? 'desc' : 'asc';
      });
      setOrderBy(property);
    },
    [orderBy]
  );

  const handleChangePage = React.useCallback((event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRowsPerPage(parseInt(event.target.value, 10));
      setPage(0);
    },
    []
  );

  const handleChangeDense = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setDense(event.target.checked);
  }, []);

  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredLogs.length) : 0;

  // Sort the full set (unavoidable), but normalize only the rendered page slice.
  const sortedLogs = React.useMemo(
    () => stableSort(filteredLogs, getComparator(order, orderBy)),
    [filteredLogs, order, orderBy]
  );

  const pagedLogs = React.useMemo(
    () => sortedLogs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [sortedLogs, page, rowsPerPage]
  );

  const actionLogsRows = React.useMemo(() => normalizeActionLogs(pagedLogs), [pagedLogs]);

  // Reset to page 0 when filters/search change so users don't land on an empty page.
  React.useEffect(() => {
    setPage(0);
  }, [searchQuery, filterState, allActionLogs]);

  const actionLogsColumnLabels = headActionLogsCells.map((item) => item.label);

  return (
    <Box sx={{ width: '100%' }} id={'action-logs'}>
      <Paper sx={{ width: '100%', mb: 2 }}>
        {isLoading ? (
          <Box
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
            }}
          >
            <TablePlaceholder rows={10} columns={actionLogsColumnLabels} />
          </Box>
        ) : (
          <>
            <TableToolbar
              title={'Data fetched at'}
              search={true}
              exportData={filteredLogs}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              filterState={filterState}
              activeFilterCount={activeFilterCount}
              onFilterChange={setFilterValue}
              onClearFilter={clearFilter}
              onClearAll={clearAll}
            />
            <TableContainer sx={{ minHeight: '30rem' }}>
              <Table
                sx={{ minWidth: 750 }}
                aria-labelledby="tableTitle"
                size={dense ? 'small' : 'medium'}
              >
                <ActionLogsTableHead
                  order={order}
                  orderBy={orderBy}
                  onRequestSort={handleRequestSort}
                />
                <TableBody>
                  {actionLogsRows.map((row: NormalizedActionLogsDataType, index) => (
                    <ActionLogsTableRow
                      key={`action-log-${index}`}
                      labelId={`action-log-${index}`}
                      row={row}
                      onOpenEntityViewer={handleOpenEntityViewerDialog}
                      onOpenErrorViewer={handleOpenErrorViewerDialog}
                    />
                  ))}
                  {emptyRows > 0 && (
                    <TableRow
                      style={{
                        height: (dense ? 33 : 53) * emptyRows,
                      }}
                    >
                      <TableCell colSpan={headActionLogsCells.length} />
                    </TableRow>
                  )}
                  {allActionLogs.length === 0 && !apiErrorFlag && (
                    <TableRow>
                      <TableCell colSpan={headActionLogsCells.length}>
                        <TableNoResults message="Oops! No results for the selected date range. Try picking a different range." />
                      </TableCell>
                    </TableRow>
                  )}
                  {actionLogsRows.length === 0 && allActionLogs.length > 0 && (
                    <TableRow>
                      <TableCell colSpan={headActionLogsCells.length}>
                        <TableNoResults />
                      </TableCell>
                    </TableRow>
                  )}
                  {apiErrorFlag && (
                    <TableRow>
                      <TableCell colSpan={headActionLogsCells.length}>
                        <DeviceDataGettingError entity={'action logs'} />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <EntityViewerDialog
              open={entityViewerDialogOpen}
              onClose={handleCloseEntityViewerDialog}
              entity={selectedEntity}
              title={'Action details'}
              fallbackMessage={'No details available'}
            />
            {actionErrorData && (
              <ErrorViewerDialog
                open={errorViewerDialogOpen}
                onClose={handleCloseErrorViewerDialog}
                title={'Error details'}
                errorSource={actionErrorData.error_source}
                errorType={actionErrorData.error_type}
                errorMessage={actionErrorData.error_message}
              />
            )}
          </>
        )}
        <TablePagination
          rowsPerPageOptions={[10, 20, 30, { value: filteredLogs.length, label: 'All' }]}
          component="div"
          count={filteredLogs.length}
          rowsPerPage={rowsPerPage}
          labelRowsPerPage="Rows per table:"
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      <FormControlLabel
        control={<Switch checked={dense} onChange={handleChangeDense} />}
        label="Dense padding"
      />
    </Box>
  );
}
